import type {
  ElectricalSnapshot,
  NodePotential,
  WorkshopProject,
  DeviceRuntimeState,
} from '../model/types';
import {
  DisjointSet,
  getDeviceInternalBridges,
  makeTerminalKey,
} from './ElectricalGraph';

export class ElectricalSolver {
  private contactorStates = new Map<string, { main: boolean; '13-14': boolean; '21-22': boolean }>();

  public reset(): void {
    this.contactorStates.clear();
  }

  /**
   * Bir projeyi elektriksel olarak çözerek anlık gerilim ve cihaz durumlarını hesaplar
   */
  public solve(project: WorkshopProject, simTimeMs = 0): ElectricalSnapshot {
    let hasShortCircuit = false;
    let shortCircuitMessage: string | undefined = undefined;

    // Kontaktör durumlarının ilk değerleri
    for (const dev of project.devices) {
      if (dev.type === 'contactor-3p' && !this.contactorStates.has(dev.id)) {
        this.contactorStates.set(dev.id, { main: false, '13-14': false, '21-22': true });
      }
    }

    let nodePotentials: Record<string, NodePotential> = {};
    const maxIterations = 10;
    let iteration = 0;
    let stateChanged = true;

    while (stateChanged && iteration < maxIterations) {
      iteration++;
      stateChanged = false;

      const dset = new DisjointSet();

      // 1. Tüm fiziksel kabloları iletken olarak düğümle
      for (const wire of project.wires) {
        const keyFrom = makeTerminalKey(wire.from.deviceId, wire.from.terminalId);
        const keyTo = makeTerminalKey(wire.to.deviceId, wire.to.terminalId);
        dset.union(keyFrom, keyTo);
      }

      // 2. Cihaz içi kapalı kontakları ve köprüleri düğümle
      for (const dev of project.devices) {
        const contactStates = dev.type === 'contactor-3p'
          ? (this.contactorStates.get(dev.id) || { main: false, '13-14': false, '21-22': true })
          : {};
        
        const bridges = getDeviceInternalBridges(dev, contactStates);
        for (const bridge of bridges) {
          if (bridge.isClosed) {
            dset.union(bridge.terminalA, bridge.terminalB);
          }
        }
      }

      // 3. Güç kaynağı referans terminallerini bul
      let l1Root: string | null = null;
      let l2Root: string | null = null;
      let l3Root: string | null = null;
      let nRoot: string | null = null;
      let peRoot: string | null = null;

      for (const dev of project.devices) {
        if (dev.type === 'power-source') {
          l1Root = dset.find(makeTerminalKey(dev.id, 'L1'));
          l2Root = dset.find(makeTerminalKey(dev.id, 'L2'));
          l3Root = dset.find(makeTerminalKey(dev.id, 'L3'));
          nRoot = dset.find(makeTerminalKey(dev.id, 'N'));
          peRoot = dset.find(makeTerminalKey(dev.id, 'PE'));
          break;
        }
      }

      // 4. Kısa devre kontrolleri (Faz-Nötr, Faz-Faz, Faz-Toprak)
      if (l1Root && nRoot && l1Root === nRoot) {
        hasShortCircuit = true;
        shortCircuitMessage = 'Kısa Devre! L1 Fazı ile Nötr hattı doğrudan bağlandı.';
      } else if (l2Root && nRoot && l2Root === nRoot) {
        hasShortCircuit = true;
        shortCircuitMessage = 'Kısa Devre! L2 Fazı ile Nötr hattı doğrudan bağlandı.';
      } else if (l3Root && nRoot && l3Root === nRoot) {
        hasShortCircuit = true;
        shortCircuitMessage = 'Kısa Devre! L3 Fazı ile Nötr hattı doğrudan bağlandı.';
      } else if (l1Root && l2Root && l1Root === l2Root) {
        hasShortCircuit = true;
        shortCircuitMessage = 'Faz-Faz Kısa Devre! L1 ile L2 fazları doğrudan çakıştı.';
      } else if (l1Root && peRoot && l1Root === peRoot) {
        hasShortCircuit = true;
        shortCircuitMessage = 'Gövde Kaçağı / Toprak Kısa Devresi! L1 fazı doğrudan PE hattına bağlandı.';
      }

      // Kısa devre varsa koruma sigortasını attır ve enerjiyi kes
      if (hasShortCircuit) {
        for (const dev of project.devices) {
          if (dev.type === 'mcb-1p' || dev.type === 'mcb-3p') {
            dev.props['tripped'] = true;
            dev.props['closed'] = false;
          }
        }
        break; // Kısa devrede döngüyü kır
      }

      // 5. Her terminalin potansiyelini ata
      nodePotentials = {};
      for (const dev of project.devices) {
        for (const term of dev.terminals) {
          const key = makeTerminalKey(dev.id, term.id);
          const root = dset.find(key);

          if (l1Root && root === l1Root) {
            nodePotentials[key] = 'L1';
          } else if (l2Root && root === l2Root) {
            nodePotentials[key] = 'L2';
          } else if (l3Root && root === l3Root) {
            nodePotentials[key] = 'L3';
          } else if (nRoot && root === nRoot) {
            nodePotentials[key] = 'N';
          } else if (peRoot && root === peRoot) {
            nodePotentials[key] = 'PE';
          } else {
            nodePotentials[key] = 'FLOATING';
          }
        }
      }

      // 6. Kontaktör bobinlerini değerlendir (A1 - A2)
      for (const dev of project.devices) {
        if (dev.type === 'contactor-3p') {
          const a1Key = makeTerminalKey(dev.id, 'A1');
          const a2Key = makeTerminalKey(dev.id, 'A2');
          const potA1 = nodePotentials[a1Key] || 'FLOATING';
          const potA2 = nodePotentials[a2Key] || 'FLOATING';

          const isPhase = (p: NodePotential) => p === 'L1' || p === 'L2' || p === 'L3';
          const isNeutral = (p: NodePotential) => p === 'N';

          // 230V gerilim: bir uçta Faz, diğer uçta Nötr varsa bobin çeker
          const energized = (isPhase(potA1) && isNeutral(potA2)) || (isNeutral(potA1) && isPhase(potA2));
          
          const currentContacts = this.contactorStates.get(dev.id)!;
          if (currentContacts.main !== energized) {
            this.contactorStates.set(dev.id, {
              main: energized,
              '13-14': energized,
              '21-22': !energized,
            });
            stateChanged = true;
          }
        }
      }
    }

    // 7. Cihazların anlık durumlarını ve çıkışlarını hesapla
    const deviceStates: Record<string, DeviceRuntimeState> = {};
    for (const dev of project.devices) {
      if (dev.type === 'contactor-3p') {
        const cState = this.contactorStates.get(dev.id) || { main: false, '13-14': false, '21-22': true };
        deviceStates[dev.id] = {
          energized: cState.main,
          contactsClosed: {
            'main': cState.main,
            '13-14': cState['13-14'],
            '21-22': cState['21-22'],
          },
        };
      } else if (dev.type === 'pilot-lamp') {
        const x1Key = makeTerminalKey(dev.id, 'X1');
        const x2Key = makeTerminalKey(dev.id, 'X2');
        const p1 = nodePotentials[x1Key];
        const p2 = nodePotentials[x2Key];
        const isPhase = (p: NodePotential | undefined) => p === 'L1' || p === 'L2' || p === 'L3';
        const isLit = (isPhase(p1) && p2 === 'N') || (isPhase(p2) && p1 === 'N');
        deviceStates[dev.id] = {
          energized: isLit,
          contactsClosed: {},
        };
      } else if (dev.type === 'motor-3phase') {
        const u1Pot = nodePotentials[makeTerminalKey(dev.id, 'U1')];
        const v1Pot = nodePotentials[makeTerminalKey(dev.id, 'V1')];
        const w1Pot = nodePotentials[makeTerminalKey(dev.id, 'W1')];
        
        // 3 fazın tamamı eksiksiz ulaştı mı?
        const isRunning = u1Pot === 'L1' && v1Pot === 'L2' && w1Pot === 'L3';
        dev.props['running'] = isRunning;
        deviceStates[dev.id] = {
          motorRunning: isRunning,
          contactsClosed: {},
          currentAmps: isRunning ? (Number(dev.props['ratedCurrentAmps']) || 3.4) : 0,
        };
      } else if (dev.type === 'thermal-relay') {
        const isTripped = dev.props['tripped'] === true;
        deviceStates[dev.id] = {
          tripped: isTripped,
          contactsClosed: {
            '95-96': !isTripped,
            '97-98': isTripped,
          },
        };
      } else if (dev.type === 'mcb-1p' || dev.type === 'mcb-3p') {
        const isClosed = !dev.props['tripped'] && dev.props['closed'] !== false;
        deviceStates[dev.id] = {
          tripped: dev.props['tripped'] === true,
          contactsClosed: { main: isClosed },
        };
      } else if (dev.type === 'pushbutton-nc' || dev.type === 'pushbutton-no') {
        deviceStates[dev.id] = {
          pressed: dev.props['pressed'] === true,
          contactsClosed: {},
        };
      } else {
        deviceStates[dev.id] = { contactsClosed: {} };
      }
    }

    // 8. Kabloların enerjili olup olmadığını belirle
    const wireEnergized: Record<string, boolean> = {};
    for (const wire of project.wires) {
      const fromPot = nodePotentials[makeTerminalKey(wire.from.deviceId, wire.from.terminalId)];
      const toPot = nodePotentials[makeTerminalKey(wire.to.deviceId, wire.to.terminalId)];
      
      const hasEnergy = fromPot === 'L1' || fromPot === 'L2' || fromPot === 'L3' ||
                        toPot === 'L1' || toPot === 'L2' || toPot === 'L3';
      wireEnergized[wire.id] = hasEnergy;
    }

    return {
      simTimeMs,
      nodePotentials,
      deviceStates,
      wireEnergized,
      hasShortCircuit,
      ...(shortCircuitMessage && { shortCircuitMessage }),
    };
  }
}
